var __assign = (this && this.__assign) || Object.assign || function(t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
        s = arguments[i];
        for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
            t[p] = s[p];
    }
    return t;
};
import { memoize } from "lodash";
import { IdMenuBalancing } from "../constants/IdMenuBalancing";
import { IdWebUiDefinitionCategories } from "../constants/IdWebUiDefinitionCategories";
import { IdLibraryCategory } from "../features/library/IdLibraryCategory";
import { IdStoreCategory } from "../features/store/IdStoreCategory";
import { utilBalancing } from "../utils/utilBalancing";
export var selectUiDefinitions = function () { return utilBalancing.getBalancing().UiDefinitions; };
export var selectUiDefinition = memoize(function (id) { return selectUiDefinitions().get(id); });
export var selectUiDefinitionCategories = memoize(function (id) {
    var definition = selectUiDefinition(id);
    if (definition == undefined || definition.category == undefined) {
        return [];
    }
    return definition.category;
});
export var selectUiDefinitionCategory = function (uiDefinitionId, categoryId) {
    var definition = selectUiDefinition(uiDefinitionId);
    if (definition == undefined) {
        return undefined;
    }
    return definition.category.find(function (category) { return category.id === categoryId; });
};
export var selectUiDefinitionTournamentSubCategories = memoize(function () {
    var categories = selectUiDefinitionCategories(IdMenuBalancing.Tournament);
    return categories.filter(function (category) { return category.leagueReward != undefined && category.leagueReward.length > 0; });
});
export var selectUiDefinitionTournamentSubCategory = memoize(function (id) {
    var pages = selectUiDefinitionTournamentSubCategories();
    return pages.find(function (element) { return element.id === id; });
});
export var selectBuildingsCategoryMap = memoize(function () {
    var buildMenuMap = {};
    var buildingCategories = selectUiDefinition(IdMenuBalancing.Build).category;
    buildingCategories.forEach(function (category) {
        var buildingItem = category.buildingItem;
        buildMenuMap[category.id] = buildingItem
            ? buildingItem.map(function (building) { return building.buildingId; })
            : [];
    });
    return buildMenuMap;
});
// don't memoize, lodash memoize only checks memoization on first param!!!
export var selectCategoryPosition = function (menuId, tabId, customTabs) {
    var tabs = customTabs ? customTabs : selectUiDefinitionCategories(menuId);
    return tabs.findIndex(function (element) { return element.id === tabId; });
};
export var selectLibraryResearches = memoize(function () {
    var uiDefinitions = selectUiDefinition(IdMenuBalancing.Library);
    var farmCategory = uiDefinitions.category.find(function (category) { return category.id === IdLibraryCategory.MainFarm; });
    if (farmCategory === undefined) {
        return [];
    }
    return farmCategory.researchItem.map(function (research) {
        return research.researchId;
    });
});
export var selectStoreCategoryDefaultIndex = memoize(function () {
    var uiDefinitions = selectUiDefinition(IdMenuBalancing.Store);
    return uiDefinitions.category.findIndex(function (category) { return category.id === IdStoreCategory.FarmDollarPackages; });
});
export var selectRecipeShouldShowInputIcon = memoize(function (buildingId) {
    var uiDefinitions = selectUiDefinition(IdMenuBalancing.ContextMenu);
    // TODO: reconsider this.
    if (!uiDefinitions ||
        !uiDefinitions.category ||
        uiDefinitions.category.length === 0 ||
        !uiDefinitions.category[0].buildingItem) {
        throw new Error("Couldn't get context menu info from UI Definitions (id: " + IdMenuBalancing.ContextMenu + ")");
    }
    var buildingItem = uiDefinitions.category[0].buildingItem;
    var building = buildingItem.find(function (item) { return item.buildingId === buildingId; });
    return !!building;
});
export var selectNurseryUiDefinitions = memoize(function () {
    return selectUiDefinition(IdMenuBalancing.Nursery);
});
export var selectNurseryProducts = memoize(function () {
    var nurseryDefinition = selectNurseryUiDefinitions();
    var isWellFormatted = nurseryDefinition != undefined && nurseryDefinition.category != undefined;
    return isWellFormatted
        ? nurseryDefinition.category.find(function (element) { return element.id === IdWebUiDefinitionCategories.NurseryProducts; })
        : undefined;
});
export var selectNurseryLevel = memoize(function (level) {
    var nurseryLevel = selectNurseryProducts();
    var isLevelInvalid = nurseryLevel == undefined ||
        nurseryLevel.buildingStage == undefined ||
        level > nurseryLevel.buildingStage.length;
    if (isLevelInvalid) {
        throw new Error("Nursery levels don't match inside balancing");
    }
    return nurseryLevel.buildingStage[level - 1];
});
export var selectUnlockedNurseryRecipesByLevel = memoize(function (level) {
    var nurseryLevel = selectNurseryLevel(level).recipeItem || [];
    return nurseryLevel.map(function (element) {
        return __assign({}, element, { isUnlocked: true, requiredLevel: level });
    });
});
export var selectLockedNurseryRecipesByLevel = memoize(function (level) {
    var nurseryLevel = selectNurseryLevel(level);
    var lockedRecipes = nurseryLevel.lockedRecipeItem || [];
    return lockedRecipes.map(function (element) {
        return __assign({}, element, { isUnlocked: false, requiredLevel: selectUnlockLevelFromRecipeId(element.recipeId, level + 1) });
    });
});
export var selectAllNurseryRecipesByLevel = memoize(function (level) { return selectUnlockedNurseryRecipesByLevel(level).concat(selectLockedNurseryRecipesByLevel(level)); });
export var selectNurseryMaxLevel = memoize(function () {
    var nurseryProducts = selectNurseryProducts();
    return nurseryProducts && nurseryProducts.buildingStage
        ? nurseryProducts.buildingStage.length
        : 0;
});
// Keep in mind that right now the second parameter is not memoized and that this implementation
// is such that the second parameter is jus a small time-saver which is not really needed.
export var selectUnlockLevelFromRecipeId = memoize(function (id, startLevel) {
    var maxLevel = selectNurseryMaxLevel();
    for (var i = startLevel || 2; i < maxLevel; i++) {
        var unlockedRecipes = selectUnlockedNurseryRecipesByLevel(i);
        var foundRecipe = unlockedRecipes.find(function (_a) {
            var recipeId = _a.recipeId;
            return recipeId === id;
        });
        if (foundRecipe != undefined) {
            return i;
        }
    }
    return -1;
});
export var balancingUiDefinitionsMemoizedFunctions = [
    selectUiDefinitions,
    selectUiDefinition,
    selectBuildingsCategoryMap,
    selectLibraryResearches,
    selectStoreCategoryDefaultIndex,
    selectRecipeShouldShowInputIcon,
    selectUiDefinitionCategories,
    selectUiDefinitionTournamentSubCategories,
    selectUiDefinitionTournamentSubCategory,
    selectNurseryUiDefinitions,
    selectNurseryProducts,
    selectUnlockedNurseryRecipesByLevel,
    selectNurseryLevel,
    selectLockedNurseryRecipesByLevel,
    selectNurseryMaxLevel,
    selectUnlockLevelFromRecipeId,
    selectAllNurseryRecipesByLevel,
];



//////////////////
// WEBPACK FOOTER
// ./src/balancingSelectors/selectBalancingUiDefinitions.ts
// module id = 100
// module chunks = 3